import {DocumentNode, useLazyQuery, useMutation} from '@apollo/client';
import {CursorBasedPaginationResponse, FilterType} from 'api/common';
import {boardMutations, boardQueries} from 'api/graphql-queries';
import {useAlert} from 'hooks/useAlert';
import {ResponseViewModel} from 'models';
import {
    AllUsersForBoardRequest,
    Board,
    BoardCreateEditRequest,
    BoardGellAllRequest,
    GetAllBoardResponse,
    UserBoardsResponse,
    UserIdsRemoveForBoardRequest,
} from 'models/board';
import {GetAllBoardSettingsRequest, SettingModel} from 'models/settings';
import {UserListingResponseModel} from 'models/tenant.model';
import {useCallback, useEffect} from 'react';

export const useGetAllBoardsByWorkSpaceSlug = (query: DocumentNode = boardQueries.GET_BOARDS_BY_WORKSPACE_SLUG) => {
    const [refetch, {loading, data}] = useLazyQuery<GetAllBoardResponse, BoardGellAllRequest>(query, {
        fetchPolicy: 'no-cache',
    });

    const getAllBoards = useCallback(
        (workspaceSlug: string) => {
            if (workspaceSlug) {
                refetch({variables: {workspaceSlug}});
            }
        },
        [refetch],
    );

    return {
        getAllBoards: getAllBoards,
        getAllBoardsResponse: data?.allAssigneedBoards,
        getAllBoardsLoading: loading,
    };
};

export const useGetAllBoardsForMyWork = () => {
    const [refetch, {loading, data}] = useLazyQuery<GetAllBoardResponse>(boardQueries.GET_ALL_BOARDS_FOR_MY_WORK, {
        fetchPolicy: 'no-cache',
    });

    return {
        getAllBoards: refetch,
        getAllBoardsResponse: data?.allAssigneedBoards,
        getAllBoardsLoading: loading,
    };
};

export const useAddUsersToBoard = () => {
    const [addUsersToBoard, {data, loading}] = useMutation<
        {addUsersToBoard: ResponseViewModel<string>},
        {request: UserIdsRemoveForBoardRequest}
    >(boardMutations.ADD_USERS_TO_BOARD, {
        fetchPolicy: 'no-cache',
    });
    return {
        addUsersToBoard,
        addUsersToBoardResponse: data?.addUsersToBoard,
        addUsersToBoardLoading: loading,
    };
};

export const useRemoveUsersFromBoard = () => {
    const [removeUsersFromBoard, {data, loading}] = useMutation<
        {removeUsersFromBoard: ResponseViewModel<string>},
        {request: UserIdsRemoveForBoardRequest}
    >(boardMutations.REMOVE_USERS_FROM_BOARD, {
        fetchPolicy: 'no-cache',
    });
    return {
        removeUsersFromBoard,
        removeUsersFromBoardResponse: data?.removeUsersFromBoard,
        removeUsersFromBoardLoading: loading,
    };
};

export const useGetusersByBoardId = () => {
    const [usersByBoardId, {data, loading}] = useLazyQuery<
        {usersByBoardSlug: CursorBasedPaginationResponse<UserListingResponseModel>},
        AllUsersForBoardRequest
    >(boardQueries.USERS_BY_BOARD_Slug, {
        fetchPolicy: 'no-cache',
    });
    return {
        usersByBoardId,
        usersByBoardIdResponse: data?.usersByBoardSlug,
        usersByBoardIdLoading: loading,
    };
};

export const useGetUsersOfAllBoardsByUserId = () => {
    const [usersOfAllBoardsByUserId, {data, loading}] = useLazyQuery<
        {usersOfAllBoardsByUserSlug: UserListingResponseModel[]},
        {workspaceSlug: string; where: FilterType<UserListingResponseModel>}
    >(boardQueries.USERS_OF_ALL_BOARDS_BY_USER_SLUG, {
        fetchPolicy: 'no-cache',
    });
    return {
        usersOfAllBoardsByUserId,
        usersOfAllBoardsByUserIdResponse: data?.usersOfAllBoardsByUserSlug,
        usersOfAllBoardsByUserIdLoading: loading,
    };
};

export const useUpdateBoard = () => {
    const {invokeSuccessMessage} = useAlert();
    const [update, {loading, data}] = useMutation<{updateBoard: Board}, {id: string; input: BoardCreateEditRequest}>(
        boardMutations.UPDATE_BOARD,
    );
    useEffect(() => {
        if (data && !loading) {
            invokeSuccessMessage('Note updated successfully');
        }
    }, [data, loading]);
    return {
        updateBoard: update,
        updateBoardLoading: loading,
        updateBoardResponse: data?.updateBoard,
    };
};

export const useUpdateUserBoardToBeSelected = () => {
    const [updateUserBoardToBeSelected, {data, loading}] = useMutation<
        {updateUserBoardToBeSelected: Array<UserBoardsResponse>},
        {workspaceId: string; boardId: string}
    >(boardMutations.UPDATE_USER_BOARD_TO_BE_SELECTED, {
        fetchPolicy: 'no-cache',
    });

    return {
        updateUserBoardToBeSelected,
        updateUserBoardToBeSelectedResponse: data?.updateUserBoardToBeSelected,
        updateUserBoardToBeSelectedLoading: loading,
    };
};

export const useGetAllBoardSettings = () => {
    const [getAllBoardSettings, {data, loading}] = useLazyQuery<
        {allBoardSettings: SettingModel[]},
        GetAllBoardSettingsRequest
    >(boardQueries.GET_BOARD_SETTINGS, {
        fetchPolicy: 'no-cache',
    });

    return {
        getAllBoardSettings,
        getAllBoardSettingsLoading: loading,
        getAllBoardSettingsResponse: data?.allBoardSettings,
    };
};
